import React, { useState } from 'react';

import { getServiceIdFromIntegration } from '@aline/common';
import { IconButton, Skeleton, Typography } from '@mui/material';
import { AlineButton, AlineTooltip, Spacer } from 'src/components/generic';
import { IconColors } from 'src/components/icons/icon-utils';
import { UnLinkIcon } from 'src/components/icons/UnLinkIcon';
import {
  ReconnectIntegrationButton,
  UnifiedConnection,
} from 'src/components/send-for-signature/components/AddIntegration';
import { LinkWorkflowToOpportunityDialog } from 'src/editor/toolbars/right-panel/integrations';
import type { Apollo } from 'src/gql/generated';
import type { IntegrationRecordMap } from 'src/hooks';
import { getIsApiDeckConnectionCallable, useGetApideckOpportunitySimple } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

import { StepLayout } from '../StepLayout';

const useStyles = makeStyles()(theme => ({
  actionButton: {
    padding: theme.spacing(0.5, 1.5),
    whiteSpace: 'nowrap',
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
  },
  closeButton: {
    alignSelf: 'flex-start',
    marginLeft: 'auto',
  },
  connectionDetails: {
    maxWidth: 380,
  },
  connectionRow: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    minHeight: 56,
  },
}));

type OnPickOpportunity = (
  opportunity: Apollo.ApideckOpportunityInput,
  action: 'add' | 'remove'
) => void;
type PreloadedOpportunityNames = Partial<Record<Apollo.Integration, string>>;

export function PickIntegrationOpportunity({
  connectionsToShow,
  integrationRecordMap,
  onPickOpportunity,
  preloadedOpportunityNames,
}: {
  connectionsToShow: Apollo.ApiDeckConnectionSimpleFragment[];
  integrationRecordMap: IntegrationRecordMap;
  onPickOpportunity: OnPickOpportunity;
  preloadedOpportunityNames: PreloadedOpportunityNames;
}) {
  const { classes } = useStyles();

  if (!connectionsToShow.length) return null;

  return (
    <StepLayout divider title="Integrations">
      <div className={classes.cardContent}>
        <PickOpportunity
          connectionsToShow={connectionsToShow}
          integrationRecordMap={integrationRecordMap}
          preloadedOpportunityNames={preloadedOpportunityNames}
          onPickOpportunity={onPickOpportunity}
        />
      </div>
    </StepLayout>
  );
}

function ConnectionAction({
  connection,
  integrationRecordMap,
  isConnectionCallable,
  onPickOpportunity,
  preloadedOpportunityName,
}: {
  connection: Apollo.ApiDeckConnectionSimpleFragment;
  integrationRecordMap: IntegrationRecordMap;
  isConnectionCallable: boolean;
  onPickOpportunity: OnPickOpportunity;
  preloadedOpportunityName?: string;
}) {
  const { classes } = useStyles();

  if (!isConnectionCallable)
    return (
      <ReconnectIntegrationButton
        buttonClassName={classes.actionButton}
        integration={connection.integration}
      />
    );

  return (
    <PickTemplateOpportunity
      connection={connection}
      integrationRecordMap={integrationRecordMap}
      preloadedOpportunityName={preloadedOpportunityName}
      onPickOpportunity={onPickOpportunity}
    />
  );
}

function PickOpportunity({
  connectionsToShow,
  integrationRecordMap,
  onPickOpportunity,
  preloadedOpportunityNames,
}: {
  connectionsToShow: Apollo.ApiDeckConnectionSimpleFragment[];
  integrationRecordMap: IntegrationRecordMap;
  onPickOpportunity: OnPickOpportunity;
  preloadedOpportunityNames: PreloadedOpportunityNames;
}) {
  const { classes } = useStyles();

  return (
    <>
      {connectionsToShow.map((connection, index) => {
        const isConnectionCallable = getIsApiDeckConnectionCallable(connection);

        return (
          <div key={index} className={classes.connectionRow}>
            <UnifiedConnection
              className={classes.connectionDetails}
              connection={connection}
              isConnectionCallable={isConnectionCallable}
              isRecordLinked={Boolean(integrationRecordMap[connection.integration])}
            />
            <Spacer />
            <ConnectionAction
              connection={connection}
              integrationRecordMap={integrationRecordMap}
              isConnectionCallable={isConnectionCallable}
              preloadedOpportunityName={preloadedOpportunityNames[connection.integration]}
              onPickOpportunity={onPickOpportunity}
            />
          </div>
        );
      })}
    </>
  );
}

function PickTemplateOpportunity({
  connection,
  integrationRecordMap,
  onPickOpportunity,
  preloadedOpportunityName,
}: {
  connection: Apollo.ApiDeckConnectionSimpleFragment;
  integrationRecordMap: IntegrationRecordMap;
  onPickOpportunity: OnPickOpportunity;
  preloadedOpportunityName?: string;
}) {
  const { classes } = useStyles();
  const opportunityId = integrationRecordMap[connection.integration];
  const [open, setOpen] = useState<boolean>(false);

  if (opportunityId)
    return (
      <TemplatedLinkedToOpportunity
        connection={connection}
        opportunityId={opportunityId}
        preloadedOpportunityName={preloadedOpportunityName}
        unLink={() =>
          onPickOpportunity({ integration: connection.integration, opportunityId }, 'remove')
        }
      />
    );

  return (
    <>
      <AlineButton
        buttonClassName={classes.actionButton}
        color="primary"
        variant="outlined"
        onClick={() => setOpen(true)}
      >
        Link
      </AlineButton>
      <LinkWorkflowToOpportunityDialog
        integration={connection.integration}
        open={open}
        onClose={() => setOpen(false)}
        onSubmit={async selectedOpportunityId => {
          onPickOpportunity(
            {
              integration: connection.integration,
              opportunityId: selectedOpportunityId,
            },
            'add'
          );
        }}
      />
    </>
  );
}

const useTemplatedLinkedToOpportunityStyles = makeStyles()(theme => ({
  opportunity: {
    alignItems: 'center',
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 8,
    boxSizing: 'border-box',
    display: 'flex',
    gap: theme.spacing(0.5),
    height: 32,
    justifyContent: 'center',
    maxWidth: 320,
    padding: theme.spacing(0.5, 1.5),
  },
}));

function TemplatedLinkedToOpportunity({
  connection,
  opportunityId,
  preloadedOpportunityName,
  unLink,
}: {
  connection: Apollo.ApiDeckConnectionSimpleFragment;
  opportunityId: UUID;
  unLink: () => void;
  preloadedOpportunityName?: string;
}) {
  const { classes } = useTemplatedLinkedToOpportunityStyles();
  const { apiDeckOpportunity, loading } = useGetApideckOpportunitySimple({
    skip: Boolean(preloadedOpportunityName),
    variables: {
      opportunityId,
      serviceId: getServiceIdFromIntegration(connection.integration),
    },
  });
  const opportunityName = preloadedOpportunityName ?? apiDeckOpportunity?.title ?? 'Untitled';

  if (!preloadedOpportunityName && (loading || !apiDeckOpportunity))
    return (
      <div className={classes.opportunity}>
        <Skeleton height={16} variant="text" width={160} />
      </div>
    );

  return (
    <div className={classes.opportunity}>
      <Typography noWrap variant="subtitle2">
        {opportunityName}
      </Typography>
      <AlineTooltip arrow placement="top" title={`Unlink "${opportunityName}"`}>
        <IconButton size="small" onClick={unLink}>
          <UnLinkIcon fill={IconColors.DarkGrey} />
        </IconButton>
      </AlineTooltip>
    </div>
  );
}
